import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Button,
  IconButton,
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import InventoryIcon from "@mui/icons-material/Inventory";
import BuildIcon from "@mui/icons-material/Build";
import CategoryIcon from "@mui/icons-material/Category";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ReusableFormDialog from "./FormModal/ReusableFormDialog";
import DeleteConfirmationDialog from "./FormModal/DeleteConfirmationDialog";
import ReusableDataGrid from "../Components/ReusableDataGrid";
import useVendorOptions from "../hooks/useVendorOptions";
import {
  partValidationSchema,
  receiveStockValidationSchema,
} from "./FormModal/validationSchemas";
import {
  buildPartPayload,
  createPartInitialValues,
  getPartFormFields,
  preparePartFormValues,
} from "./Parts/partFormConfig";

function MaskedBuyPriceCell({ value, formatMoney }) {
  const [showBuyPrice, setShowBuyPrice] = useState(false);
  const hasValue = value !== "" && value != null;

  if (!hasValue) return "-";

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Box component="span" sx={{ minWidth: 54 }}>
        {showBuyPrice ? formatMoney(value) : "$ ****"}
      </Box>
      <IconButton
        aria-label={showBuyPrice ? "Hide buy price" : "Show buy price"}
        size="small"
        title={showBuyPrice ? "Hide buy price" : "Show buy price"}
        onClick={(event) => {
          event.stopPropagation();
          setShowBuyPrice((prev) => !prev);
        }}
      >
        {showBuyPrice ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
}

function Parts() {
  const current_url = useLocation();
  const navigate = useNavigate();

  const receiveStockInit = {
    quantity: "",
    vendor: "",
    invoiceNo: "",
    date: new Date().toISOString().slice(0, 10),
    note: "",
  };

  const [activeTab, setActiveTab] = useState(0); // 0: Inventory, 1: Special Order
  const [search, setSearch] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [parts, setParts] = useState([]);
  const [currentItem, setCurrentItem] = useState(() =>
    createPartInitialValues(Helpers.authParentId),
  );
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);

  const [openReceiveDialog, setOpenReceiveDialog] = useState(false);
  const [receiveItem, setReceiveItem] = useState(null);
  const [receiveForm, setReceiveForm] = useState(receiveStockInit);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const receiveSubmitLock = useRef(false);
  const { getVendorFieldOptions } = useVendorOptions(navigate);

  const partFields = useMemo(
    () => getPartFormFields(getVendorFieldOptions(currentItem?.vendor), currentItem),
    [currentItem, getVendorFieldOptions],
  );

  const receiveStockFields = useMemo(
    () => [
      { name: "quantity", label: "Quantity", type: "number" },
      {
        name: "vendor",
        label: "Vendor",
        type: "select",
        options: getVendorFieldOptions(receiveForm?.vendor),
      },
      { name: "invoiceNo", label: "Invoice No.", type: "text" },
      { name: "date", label: "Date", type: "date" },
      { name: "note", label: "Note", type: "text", multiline: true, rows: 2 },
    ],
    [getVendorFieldOptions, receiveForm?.vendor],
  );

  const formatMoney = (value) => `$ ${Number(value || 0).toFixed(2)}`;

  const baseColumns = [
    {
      field: "serial",
      headerName: "Sr. #",
      width: 70,
      sortable: false,
      filterable: false,
    },
    { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
    { field: "partNumber", headerName: "Part #", width: 120 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 180 },
    {
      field: "sellPrice",
      headerName: "Sell",
      width: 100,
      renderCell: (params) => formatMoney(params.value),
    },
    { field: "vendor", headerName: "Vendor", width: 130 },
    { field: "invoiceNo", headerName: "Invoice", width: 120 },
  ];

  const inventoryColumns = [
    ...baseColumns.slice(0, 4),
    { field: "quantity", headerName: "Qty", width: 90 },
    {
      field: "buyPrice",
      headerName: "Buy",
      width: 130,
      renderCell: (params) => (
        <MaskedBuyPriceCell value={params.value} formatMoney={formatMoney} />
      ),
    },
    ...baseColumns.slice(4),
  ];

  const specialOrderColumns = [
    { field: "name", headerName: "Name", flex: 1, minWidth: 140 },
    { field: "partNumber", headerName: "Part #", width: 120 },
    {
      field: "specialOrderStatus",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const status = params.row.specialOrderStatus || "Available";
        const paletteByStatus = {
          Available: { bg: "#dcfce7", color: "#166534" },
          Assigned: { bg: "#dbeafe", color: "#1d4ed8" },
          Sold: { bg: "#ede9fe", color: "#6d28d9" },
          Returned: { bg: "#fee2e2", color: "#b91c1c" },
          "Needs Review": { bg: "#fef3c7", color: "#92400e" },
        };
        const palette = paletteByStatus[status] || {
          bg: "#e5e7eb",
          color: "#374151",
        };
        return (
          <Chip
            label={status}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: palette.bg,
              color: palette.color,
            }}
          />
        );
      },
    },
    {
      field: "specialOrderFriendlyNumber",
      headerName: "Sold Invoice",
      width: 120,
      renderCell: (params) => params.row.specialOrderFriendlyNumber || "-",
    },
    {
      field: "specialOrderCustomer",
      headerName: "Customer",
      minWidth: 160,
      renderCell: (params) => params.row.specialOrderCustomer?.name || "-",
    },
    {
      field: "invoiceDate",
      headerName: "Purchase Date",
      width: 120,
      renderCell: (params) =>
        params.row.invoiceDate ? new Date(params.row.invoiceDate).toLocaleDateString() : "-",
    },
    {
      field: "invoiceNo",
      headerName: "Purchase Invoice",
      width: 140,
      renderCell: (params) => params.row.invoiceNo || "-",
    },
    {
      field: "refundAmount",
      headerName: "Refund",
      width: 110,
      renderCell: (params) => formatMoney(params.value),
    },
    { field: "vendor", headerName: "Vendor", width: 130 },
  ];

  const getParts = useCallback(() => {
    setLoading(true);
    axios
      .get(`${Helpers.baseUrl}/parts/all/${Helpers.authParentId}`, Helpers.headers)
      .then((response) => {
        setParts(Array.isArray(response.data) ? response.data.reverse() : []);
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate]);

  const getPerms = useCallback(() => {
    const link = current_url.pathname;
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find((permission) => permission.tab_link === link);
      setPerms(perm || {});
    }
  }, [current_url.pathname]);

  useEffect(() => {
    getParts();
    getPerms();
  }, [getParts, getPerms]);

  const canCreate = Helpers.authUser.user_role == null || perms.can_create === 1;
  const canUpdate = Helpers.authUser.user_role == null || perms.can_update === 1;
  const canDelete = Helpers.authUser.user_role == null || perms.can_delete === 1;

  const inventoryParts = parts.filter((p) => (p.partType || "Inventory") === "Inventory");
  const nonInventoryParts = parts.filter((p) => (p.partType || "Inventory") === "Non Inventory");

  const searchFilter = (items) => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((p) => {
      return (
        (p.name || "").toLowerCase().includes(term) ||
        (p.partNumber || "").toLowerCase().includes(term) ||
        (p.vendor || "").toLowerCase().includes(term) ||
        (p.invoiceNo || "").toLowerCase().includes(term) ||
        (p.description || "").toLowerCase().includes(term) ||
        (p.specialOrderStatus || "").toLowerCase().includes(term) ||
        (p.specialOrderFriendlyNumber || "").toLowerCase().includes(term) ||
        (p.specialOrderCustomer?.name || "").toLowerCase().includes(term)
      );
    });
  };

  const visibleParts = searchFilter(activeTab === 0 ? inventoryParts : nonInventoryParts);

  const handleSave = (values) => {
    setBtnLoading(true);
    const addOrUpdate = isEditing ? "update" : "add";
    const partType = values.partType === "Non Inventory" ? "Non Inventory" : "Inventory";

    const payload = buildPartPayload(
      {
        ...values,
        partType,
      },
      Helpers.authParentId,
      partType,
    );

    axios
      .post(`${Helpers.baseUrl}/parts/${addOrUpdate}`, payload, Helpers.headers)
      .then(() => {
        getParts();
        setCurrentItem(createPartInitialValues(Helpers.authParentId, partType));
        setIsEditing(false);
        setOpenFormDialog(false);
        Helpers.toast("success", "Part saved successfully");
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
      })
      .finally(() => {
        setBtnLoading(false);
      });
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setCurrentItem(preparePartFormValues(item, Helpers.authParentId));
    setOpenFormDialog(true);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    setCurrentItem(
      createPartInitialValues(
        Helpers.authParentId,
        activeTab === 0 ? "Inventory" : "Non Inventory",
      ),
    );
    setOpenFormDialog(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;

    setDeleteLoading(true);
    axios
      .get(`${Helpers.baseUrl}/parts/delete/${itemToDelete._id}`, Helpers.headers)
      .then(() => {
        getParts();
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        Helpers.toast("success", "Part deleted successfully");
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  const openReceiveModal = (item) => {
    setReceiveItem(item);
    setReceiveForm({
      ...receiveStockInit,
      vendor: item.vendor || "",
    });
    setOpenReceiveDialog(true);
  };

  const handleReceiveStock = (values) => {
    if (receiveSubmitLock.current || receiveLoading) return;
    if (!receiveItem?._id) {
      Helpers.toast("error", "No item selected");
      return;
    }
    receiveSubmitLock.current = true;
    setReceiveLoading(true);

    const payload = {
      _id: receiveItem._id,
      quantity: parseInt(values.quantity, 10) || 0,
      vendor: values.vendor || "",
      invoiceNo: values.invoiceNo || "",
      date: values.date || new Date().toISOString().slice(0, 10),
      note: values.note || "",
    };

    axios
      .post(`${Helpers.baseUrl}/parts/receive-stock`, payload, Helpers.headers)
      .then((response) => {
        if (response?.data) {
          setReceiveItem(response.data);
        }
        setOpenReceiveDialog(false);
        setReceiveItem(null);
        setReceiveForm(receiveStockInit);
        getParts();
        Helpers.toast("success", "Stock received successfully");
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
      })
      .finally(() => {
        setReceiveLoading(false);
        receiveSubmitLock.current = false;
      });
  };

  const actionsRenderer = (params) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        gap: 0.5,
      }}
    >
      {activeTab === 0 && canUpdate && (
        <IconButton
          onClick={() => openReceiveModal(params.row)}
          color="info"
          size="small"
          title="Receive Stock"
        >
          <InventoryIcon fontSize="small" />
        </IconButton>
      )}
      {canUpdate && (
        <IconButton onClick={() => handleEdit(params.row)} color="success" size="small">
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDelete && (
        <IconButton onClick={() => handleDelete(params.row)} color="error" size="small">
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const columnsWithActions = [
    ...(activeTab === 0 ? inventoryColumns : specialOrderColumns),
    {
      field: "actions",
      headerName: "Actions",
      width: activeTab === 0 ? 140 : 110,
      sortable: false,
      filterable: false,
      renderCell: actionsRenderer,
    },
  ];

  const rows = visibleParts.map((item, index) => ({
    ...item,
    id: item._id,
    serial: index + 1,
  }));

  const activeTitle =
    (currentItem?.partType || (activeTab === 0 ? "Inventory" : "Non Inventory")) ===
    "Non Inventory"
      ? "Special Order Part"
      : "Inventory Part";

  return (
    <Box
      sx={{
        pt: { xs: 10, sm: 11, md: 12 },
        pb: "64px",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          maxWidth: "100% !important",
          px: { xs: 2, sm: 3, md: 4 },
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 3,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            flexShrink: 0,
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "50%",
              transform: "translate(30%, -30%)",
            },
          }}
        >
          <Breadcrumbs
            separator={
              <NavigateNextIcon fontSize="small" sx={{ color: "rgba(255,255,255,0.7)" }} />
            }
            sx={{ mb: 2 }}
          >
            <MuiLink
              component={Link}
              to="/user/dashboard"
              underline="hover"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "rgba(255,255,255,0.9)",
                fontSize: "0.875rem",
                "&:hover": { color: "white" },
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Home
            </MuiLink>
            <Typography
              sx={{
                display: "flex",
                alignItems: "center",
                color: "white",
                fontWeight: 600,
                fontSize: "0.875rem",
              }}
            >
              <CategoryIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Parts
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: "flex", alignItems: "center", position: "relative", zIndex: 1 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(10px)",
                mr: 2,
              }}
            >
              <img
                src={require("../images/icons/services.png")}
                alt="Parts"
                style={{ height: 40, width: 40, objectFit: "contain" }}
              />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                Parts Management
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                Manage inventory-tracked and special-order parts
              </Typography>
            </Box>
            <Chip
              label={`${rows.length} ${rows.length === 1 ? "item" : "items"}`}
              size="small"
              sx={{
                ml: "auto",
                background: "rgba(255, 255, 255, 0.2)",
                backdropFilter: "blur(10px)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            />
          </Box>
        </Paper>

        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              <Tabs
                value={activeTab}
                onChange={(e, v) => {
                  setActiveTab(v);
                  setSearch("");
                }}
                sx={{ minHeight: 40 }}
              >
                <Tab icon={<InventoryIcon />} iconPosition="start" label="Inventory Parts" />
                <Tab icon={<BuildIcon />} iconPosition="start" label="Special Order Parts" />
              </Tabs>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ minWidth: { md: "45%" } }}>
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddNew}
                    size="small"
                    sx={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      backgroundColor: "#28a745",
                      "&:hover": { backgroundColor: "#218838" },
                    }}
                  >
                    Add {activeTab === 0 ? "Inventory" : "Special Order"} Part
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <ReusableDataGrid
              rows={rows}
              loading={loading}
              columns={columnsWithActions}
              pageSize={10}
              pageSizeOptions={[10, 25, 50, 100]}
              noRowsMessage={`No ${activeTab === 0 ? "inventory parts" : "special order parts"} available to display`}
              autoHeight={false}
              sx={{
                flex: 1,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.04)",
                },
              }}
            />
          </Box>
        </Paper>
      </Container>

      <ReusableFormDialog
        open={openFormDialog}
        onClose={() => {
          setOpenFormDialog(false);
          setCurrentItem(createPartInitialValues(Helpers.authParentId));
          setIsEditing(false);
        }}
        title={isEditing ? `Edit ${activeTitle}: ${currentItem.name || ""}` : `Add New ${activeTitle}`}
        initialValues={currentItem}
        validationSchema={partValidationSchema}
        fields={partFields}
        onSubmit={handleSave}
        loading={btnLoading}
      />

      <ReusableFormDialog
        open={openReceiveDialog}
        onClose={() => {
          setOpenReceiveDialog(false);
          setReceiveItem(null);
          setReceiveForm(receiveStockInit);
        }}
        title={`Receive Stock: ${receiveItem?.name || ""}`}
        initialValues={receiveForm}
        validationSchema={receiveStockValidationSchema}
        fields={receiveStockFields}
        onSubmit={handleReceiveStock}
        loading={receiveLoading}
        maxWidth="md"
      >
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Purchase History
        </Typography>
        <TableContainer sx={{ maxHeight: 280 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(receiveItem?.purchaseHistory || []).length > 0 ? (
                [...(receiveItem?.purchaseHistory || [])]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map((entry, idx) => (
                    <TableRow key={`${entry?.date || idx}-${idx}`}>
                      <TableCell>{entry?.date ? new Date(entry.date).toLocaleDateString() : "-"}</TableCell>
                      <TableCell align="right">{entry?.quantity ?? "-"}</TableCell>
                      <TableCell>{entry?.vendor || "-"}</TableCell>
                      <TableCell>{entry?.invoiceNo || "-"}</TableCell>
                      <TableCell>{entry?.note || "-"}</TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No purchase history available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ReusableFormDialog>

      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        itemName={itemToDelete?.name}
        loading={deleteLoading}
        warningMessage="This action cannot be undone. All associated records and receipts will remain but will no longer be linked to this part."
      />
    </Box>
  );
}

export default Parts;
